import { type FC, useCallback, useState } from 'react'
import classNames from 'classnames'
import { COLLAPSED_QUESTION_COUNT, MIN_QUESTION_COUNT, PREP_QUESTIONS_SUBTITLE, PREP_QUESTIONS_TITLE } from './prepQuestionsConfig'
import { MeetingInsightsCard } from '../MeetingInsights/MeetingInsightsCard'
import styles from '../MeetingInsights/MeetingInsights.module.scss'
import { type LikelyQuestion } from '../../../../../../store/questionInsights/questionInsights'
import { Button } from '../../../../../../components/Button/Button'
import { TrackEvent, useTracking } from '../../../common/tracking'

const PrepQuestionsEmptyState: FC = () => {
  return (
    <div className='d-flex flex-column align-items-center zocks-gap-1 zocks-p-2.0 mx-auto w-100'>
      <div
        className={classNames(
          'd-flex align-items-center justify-content-center flex-shrink-0 zocks-bg-color-gray-100 border zocks-border-color-gray-200 zocks-border-0.5 zocks-text-primary',
          styles.iconBox,
        )}
      >
        <i className='bi bi-question-circle fs-6' aria-hidden />
      </div>
      <div className='d-flex flex-column align-items-center text-center zocks-gap-0.25 w-100'>
        <p className='m-0 fw-bold paragraph-medium zocks-text-gray-900'>No questions to show yet</p>
        <p className='m-0 paragraph-small zocks-text-gray-700'>Questions appear here as they come up across your meetings.</p>
      </div>
    </div>
  )
}

interface PrepQuestionsCardProps {
  questions: LikelyQuestion[]
}

export const PrepQuestionsCard: FC<PrepQuestionsCardProps> = ({ questions }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { trackingClient } = useTracking()

  const toggleExpanded = useCallback(() => {
    if (!isExpanded) {
      trackingClient.track({ eventName: TrackEvent.MEETING_PREP_LIKELY_QUESTIONS_EXPANDED })
    }
    setIsExpanded(!isExpanded)
  }, [isExpanded, trackingClient])

  const visibleQuestions = isExpanded ? questions : questions.slice(0, COLLAPSED_QUESTION_COUNT)
  const isToggleVisible = questions.length > COLLAPSED_QUESTION_COUNT

  return (
    <MeetingInsightsCard title={PREP_QUESTIONS_TITLE} subtitle={PREP_QUESTIONS_SUBTITLE} subtitlePlacement='below'>
      {questions.length < MIN_QUESTION_COUNT ? (
        <PrepQuestionsEmptyState />
      ) : (
        <div className='d-flex flex-column zocks-gap-1.5 w-100'>
          <div className='d-flex flex-column zocks-gap-1 w-100'>
            {visibleQuestions.map(({ id, text }) => (
              <p key={id} className='m-0 py-1 ps-2 border-start border-2 zocks-border-color-primary paragraph-medium zocks-text-gray-900'>
                {text}
              </p>
            ))}
          </div>
          {isToggleVisible ? (
            <Button
              variant='primaryLinkButton'
              className='button-medium text-decoration-none align-self-start d-flex align-items-center zocks-gap-0.25 p-0'
              onClick={toggleExpanded}
              aria-expanded={isExpanded}
            >
              {isExpanded ? 'Show less' : 'Show more questions'}
              <i className='bi bi-chevron-expand fs-6' aria-hidden />
            </Button>
          ) : null}
        </div>
      )}
    </MeetingInsightsCard>
  )
}
